import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { UserPlus, DollarSign, Monitor, Trophy, Bot, Heart, Rocket, X, ArrowRight } from 'lucide-react';

const platforms = [
  {
    name: 'More Leads', Icon: UserPlus, color: '#00f0ff', angle: -90.0,
    tagline: 'Turn strangers into qualified conversations.',
    body: 'We build multi-touch capture systems — landing pages, lead magnets, retargeting, LinkedIn conversation ads — that keep the pipeline full without you chasing.',
    proof: { metric: '+312%', label: 'qualified leads MoM for a B2B SaaS client in Q1' },
  },
  {
    name: 'More Sales', Icon: DollarSign, color: '#10d97f', angle: -38.6,
    tagline: 'Close the leads you already paid to attract.',
    body: 'Sales enablement playbooks, CRM automations, checkout optimizations, and abandoned-cart flows that turn interest into revenue — not just clicks.',
    proof: { metric: '+186%', label: 'closed-won revenue for a DTC fashion brand in 6 months' },
  },
  {
    name: 'Better Websites', Icon: Monitor, color: '#8a2be2', angle: 12.9,
    tagline: 'A site that loads fast, ranks high, and converts.',
    body: 'Rebuilds and CRO on Next.js/React stacks — Core Web Vitals in the green, 100 PageSpeed, structured data for SEO/AEO, and a design that actually sells.',
    proof: { metric: '99/100', label: 'PageSpeed score + 42% conversion lift on relaunch' },
  },
  {
    name: 'Higher Rankings', Icon: Trophy, color: '#ffb400', angle: 64.3,
    tagline: 'Own page 1 for the searches that pay you.',
    body: 'Keyword strategy, topical authority, technical SEO, content clusters, and LLM-optimized answers (LLMO/AEO) so you win both Google and ChatGPT.',
    proof: { metric: '47', label: 'target keywords ranked page 1 within 6 months' },
  },
  {
    name: 'AI Automation', Icon: Bot, color: '#a855f7', angle: 115.7,
    tagline: 'Let AI handle the busywork, not the buyer.',
    body: 'LLM-powered lead scoring, auto-personalized outbound, chatbot triage, content drafting, and reporting workflows — production-grade, not gimmicks.',
    proof: { metric: '84%', label: 'of inbound leads auto-qualified before a rep touches them' },
  },
  {
    name: 'Customer Retention', Icon: Heart, color: '#f43f5e', angle: 167.1,
    tagline: 'The cheapest customer is the one you already have.',
    body: 'Lifecycle email flows, loyalty programs, win-back sequences, and NPS-driven product feedback loops — repeat revenue on autopilot.',
    proof: { metric: '+42%', label: 'repeat-purchase rate for an e-commerce client in 90 days' },
  },
  {
    name: 'Brand Growth', Icon: Rocket, color: '#ff0080', angle: 218.6,
    tagline: 'From "who?" to top-of-mind — measurably.',
    body: 'Brand positioning, thought leadership content, PR + earned media, YouTube presence, and community building that compounds every quarter.',
    proof: { metric: '+216%', label: 'branded search + share-of-voice in a 12-month brand build' },
  },
];

const OrbitNode = ({ platform, progress, index, hubSize, onClick }) => {
  const Icon = platform.Icon;
  const activateAt = 0.08 + (index / platforms.length) * 0.5;
  const opacity = useTransform(progress, [activateAt - 0.05, activateAt + 0.02], [0.15, 1]);
  const scale   = useTransform(progress, [activateAt - 0.05, activateAt + 0.02], [0.7, 1]);
  const glowOp  = useTransform(progress, [activateAt - 0.05, activateAt + 0.02], [0, 0.7]);
  const rad = (platform.angle * Math.PI) / 180;
  const x = Math.cos(rad) * hubSize;
  const y = Math.sin(rad) * hubSize;

  return (
    <motion.div
      className="absolute top-1/2 left-1/2"
      style={{ x, y, opacity, scale, translateX: '-50%', translateY: '-50%' }}
    >
      <motion.div
        className="absolute inset-0 rounded-full blur-2xl"
        style={{ backgroundColor: platform.color, opacity: glowOp, scale: 1.5 }}
      />
      <button
        onClick={onClick}
        aria-label={`Learn how RANSEN delivers ${platform.name}`}
        data-testid={`orbit-node-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
        className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center border backdrop-blur-md transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer"
        style={{
          background: `linear-gradient(135deg, ${platform.color}25, ${platform.color}05)`,
          borderColor: `${platform.color}55`,
          boxShadow: `0 0 30px ${platform.color}40, inset 0 0 20px ${platform.color}15`,
        }}
      >
        <Icon size={22} color={platform.color} strokeWidth={1.75} />
      </button>
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-[10px] font-mono uppercase tracking-[0.15em] text-white/60 whitespace-nowrap pointer-events-none">
        {platform.name}
      </div>
    </motion.div>
  );
};

const OutcomeModal = ({ outcome, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  if (!outcome) return null;
  const Icon = outcome.Icon;
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="outcome-modal"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden border backdrop-blur-2xl"
        style={{
          borderColor: `${outcome.color}55`,
          boxShadow: `0 0 80px ${outcome.color}55, inset 0 0 40px ${outcome.color}15`,
          background: `linear-gradient(135deg, ${outcome.color}18, rgba(3,0,20,0.9))`,
        }}
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center transition-colors z-10"
          data-testid="outcome-modal-close"
        >
          <X size={16} className="text-white/70" />
        </button>

        <div className="p-8 md:p-10">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 border"
            style={{
              background: `linear-gradient(135deg, ${outcome.color}30, ${outcome.color}08)`,
              borderColor: `${outcome.color}60`,
              boxShadow: `0 0 40px ${outcome.color}40`,
            }}
          >
            <Icon size={24} color={outcome.color} strokeWidth={1.75} />
          </div>

          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            {outcome.name}
          </h3>
          <p className="text-white/80 text-base md:text-lg font-body leading-snug mb-5">
            {outcome.tagline}
          </p>
          <p className="text-white/60 text-sm md:text-[15px] font-body leading-relaxed mb-6">
            {outcome.body}
          </p>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5 mb-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mb-2">Recent proof</div>
            <div className="flex items-baseline gap-3">
              <div
                className="text-3xl md:text-4xl font-heading font-black tabular-nums"
                style={{ color: outcome.color }}
              >
                {outcome.proof.metric}
              </div>
              <div className="text-white/60 text-xs md:text-sm leading-snug">{outcome.proof.label}</div>
            </div>
          </div>

          <a
            href="#contact"
            onClick={onClose}
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-void font-semibold text-sm hover:bg-white/90 transition-colors"
            data-testid="outcome-modal-cta"
          >
            Get this for your business
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

const StreamLine = ({ x2, y2, color, progress, activateAt, index }) => {
  const opacity = useTransform(progress, [activateAt - 0.05, activateAt + 0.02], [0, 0.85]);
  return (
    <motion.g style={{ opacity }}>
      <line x1={0} y1={0} x2={x2} y2={y2} stroke={color} strokeOpacity="0.2" strokeWidth="1" />
      <line
        x1={0} y1={0} x2={x2} y2={y2}
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeDasharray="4 20"
        style={{ animation: `flow-${index} 2s linear infinite` }}
      />
      <style>{`@keyframes flow-${index} { to { stroke-dashoffset: -48; } }`}</style>
    </motion.g>
  );
};

const DataStreams = ({ progress, hubSize }) => (
  <svg
    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
    width={hubSize * 2.4} height={hubSize * 2.4}
    viewBox={`${-hubSize * 1.2} ${-hubSize * 1.2} ${hubSize * 2.4} ${hubSize * 2.4}`}
    style={{ overflow: 'visible' }}
  >
    {platforms.map((p, i) => {
      const rad = (p.angle * Math.PI) / 180;
      return (
        <StreamLine
          key={p.name}
          x2={Math.cos(rad) * hubSize}
          y2={Math.sin(rad) * hubSize}
          color={p.color}
          progress={progress}
          activateAt={0.08 + (i / platforms.length) * 0.5}
          index={i}
        />
      );
    })}
  </svg>
);

const CentralHub = () => (
  <div className="relative">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="absolute top-1/2 left-1/2 rounded-full border border-brand/40"
        style={{
          width: 140 + i * 44, height: 140 + i * 44,
          marginLeft: -(70 + i * 22), marginTop: -(70 + i * 22),
          animation: `pulse-ring 3s ease-out ${i}s infinite`,
        }}
      />
    ))}
    <style>{`
      @keyframes pulse-ring {
        0%   { transform: scale(0.6); opacity: 0.9; }
        100% { transform: scale(1.6); opacity: 0; }
      }
      @keyframes spin-slow { to { transform: rotate(360deg); } }
    `}</style>
    <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center">
      <div className="absolute inset-0 rounded-full border-2 border-brand/60"
           style={{ boxShadow: '0 0 80px rgba(138,43,226,0.7), inset 0 0 40px rgba(138,43,226,0.3)' }} />
      <div className="absolute inset-3 rounded-full bg-gradient-to-br from-brand via-accent-pink to-brand-dark flex items-center justify-center">
        <span className="text-white text-4xl md:text-5xl font-heading font-black tracking-tighter"
              style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}>R</span>
      </div>
      <div className="absolute inset-[-10px] rounded-full border border-dashed border-white/20"
           style={{ animation: 'spin-slow 30s linear infinite' }} />
    </div>
  </div>
);

const ScrollFlow3D = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const [openOutcome, setOpenOutcome] = useState(null);

  const [hubR, setHubR] = useState(220);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w < 640) setHubR(105);
      else if (h < 820) setHubR(150);
      else if (h < 900) setHubR(170);
      else if (h < 1000) setHubR(200);
      else setHubR(230);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // CTA button opacity — fades in over second half of the section
  const ctaOpacity = useTransform(scrollYProgress, [0.55, 0.75], [0, 1]);
  const ctaY = useTransform(scrollYProgress, [0.55, 0.75], [12, 0]);

  return (
    <>
    <section
      ref={containerRef}
      className="relative bg-void"
      style={{ height: '260vh' }}
      data-testid="scroll-flow-3d-section"
    >
      <div className="sticky top-0 h-screen w-full flex flex-col overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-brand/[0.08] blur-[140px]" />
          <div className="absolute top-[30%] right-[10%] w-[400px] h-[400px] rounded-full bg-accent-pink/10 blur-[120px]" />
          <div className="absolute bottom-[20%] left-[10%] w-[350px] h-[350px] rounded-full bg-accent-cyan/[0.08] blur-[120px]" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: 'radial-gradient(circle at 50% 50%, transparent 20%, #030014 85%)' }} />

        {/* Header */}
        <div className="relative z-20 pt-16 md:pt-20 pb-2 md:pb-4 px-6 text-center shrink-0">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[10px] md:text-xs font-mono tracking-[0.3em] uppercase font-semibold mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live · The RANSEN Growth Engine
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] max-w-4xl mx-auto">
            Seven outcomes.{' '}
            <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">
              One growth engine.
            </span>
          </h2>
          <p className="text-white/60 text-xs md:text-sm mt-3 max-w-2xl mx-auto font-body">
            More leads, more sales, better websites, higher rankings, AI automation, retention, brand — every outcome your business actually cares about, running as one connected system.
          </p>
          <p
            className="text-white/80 text-[11px] md:text-xs mt-2 font-mono tracking-[0.2em] uppercase whitespace-nowrap overflow-hidden text-ellipsis"
            data-testid="growth-engine-tap-hint"
          >
            Tap any outcome to see how →
          </p>
        </div>

        {/* Hub + orbits */}
        <div className="relative z-10 flex-1 flex items-center justify-center min-h-0 pt-6 md:pt-8 pb-4">
          <div className="relative" style={{ width: hubR * 2.7, height: hubR * 2.7 }}>
            <DataStreams progress={scrollYProgress} hubSize={hubR} />
            {platforms.map((p, i) => (
              <OrbitNode
                key={p.name}
                platform={p}
                progress={scrollYProgress}
                index={i}
                hubSize={hubR}
                onClick={() => setOpenOutcome(p)}
              />
            ))}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <CentralHub />
            </div>
          </div>
        </div>

        {/* Anchored Book a Call CTA (fades in as scroll progresses) */}
        <motion.div
          className="relative z-30 flex items-center justify-center pb-8 md:pb-10 shrink-0"
          style={{ opacity: ctaOpacity, y: ctaY }}
        >
          <a
            href="#contact"
            data-testid="growth-engine-cta"
            className="group inline-flex items-center gap-3 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-semibold text-white text-sm md:text-base border border-white/15 backdrop-blur-md transition-all duration-200 hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-white/40"
            style={{
              background: 'linear-gradient(135deg, rgba(138,43,226,0.9), rgba(255,0,128,0.9))',
              boxShadow: '0 8px 32px rgba(138,43,226,0.4), 0 0 60px rgba(255,0,128,0.25)',
            }}
          >
            Book a free strategy call
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>

    <AnimatePresence>
      {openOutcome && (
        <OutcomeModal outcome={openOutcome} onClose={() => setOpenOutcome(null)} />
      )}
    </AnimatePresence>
    </>
  );
};

export default ScrollFlow3D;
